import React, { Fragment } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import PropTypes from 'prop-types'
import { Alert, AlertTitle } from '@material-ui/lab'
import {
  Box,
  FormControlLabel,
  FormHelperText,
  Switch,
  Typography,
} from '@material-ui/core'

import { ExpirationPickerComponent, SectionCard, Select } from 'components'
import { DocumentApprovalAttachment, PermissionSection } from './components'
import { InformationSection } from '..'

import theme from 'theme'
import constants from 'constants/index'

const DocumentSettings = ({
  currentDocument,
  type,
  availableUsers,
  departments,
  permissionsIsLoading,
  usersIsLoading,
  departmentsIsLoading,
}) => {
  const {
    PUBLICATION_CONTROL_ITEMS,
    ACTIVE_STATUS_ID,
    DRAFT_STATUS_ID,
    AWAITING_APPROVAL_STATUS_ID,
    WRITTEN_CONTENT_KIND,
    EXPIRED_STATUS_ID,
    ATTACHMENT_CONTENT_KIND,
    CONTENT_KIND_ITEMS,
  } = constants.documents

  const { control, errors, watch } = useFormContext()

  const contentKind = watch('contentKind')
  const dueDate = watch('dueDate')

  const isAttachmentContent = contentKind === ATTACHMENT_CONTENT_KIND
  const isWrittenContent = contentKind === WRITTEN_CONTENT_KIND

  const isShow = type === 'show'
  const isCreate = type === 'create'

  const createdBySystem = currentDocument?.createdBySystem
  const collectAccepted = currentDocument?.collectAccepted
  const isActive = currentDocument?.status === ACTIVE_STATUS_ID

  return (
    <Box display="flex" flexDirection="column" gridGap={theme.spacing(4)}>
      <SectionCard
        title="Configurações do documento"
        py={2}
        id={constants.tours.documents.MANAGEMENT_STEP_2}
        display="flex"
        flexDirection="column"
        gridGap={theme.spacing(2)}
      >
        <Box display="flex" flexDirection="column" gridGap={theme.spacing(2)}>
          {!isActive && !isCreate && (
            <Alert variant="standard" severity="info">
              Este conteúdo&nbsp;
              {currentDocument?.status === DRAFT_STATUS_ID
                ? 'é um rascunho'
                : currentDocument?.status === AWAITING_APPROVAL_STATUS_ID
                ? 'está aguardando aprovação'
                : 'esta inativado'}
              &nbsp;e não poderá ser visível no canal de atendimento. Para
              visualizar a última versão ativa, consulte o histórico de versões
              na aba de Histórico, se disponível.
            </Alert>
          )}
          {isCreate && (
            <Fragment>
              <Controller
                as={
                  <Select
                    label="Tipo do conteúdo"
                    items={CONTENT_KIND_ITEMS}
                    error={!!errors?.contentKind}
                    helperText={errors?.contentKind?.message}
                  />
                }
                name="contentKind"
                control={control}
              />
              {isAttachmentContent && (
                <Alert variant="standard" severity="info">
                  <AlertTitle>Envio de conteúdo como arquivo</AlertTitle>
                  Arquivos enviados não serão disponibilizados nos canais de
                  comunicação, tampouco utilizados para fins de coleta de
                  aceites. Ressaltamos ainda que, para esse tipo de conteúdo,
                  não será mantido histórico de versões.
                </Alert>
              )}
            </Fragment>
          )}
        </Box>
        {isWrittenContent && (
          <Box display="flex" flexDirection="column">
            <Typography variant="h6" gutterBottom>
              Controle de publicação
            </Typography>
            {PUBLICATION_CONTROL_ITEMS.map((item, index) => {
              if (!createdBySystem && item?.name === 'allowAutomaticEditing')
                return null

              return (
                <Fragment key={index}>
                  <Controller
                    as={
                      <FormControlLabel
                        control={<Switch />}
                        label={item?.label}
                        disabled={isShow}
                      />
                    }
                    control={control}
                    name={item?.name}
                  />
                  {!!errors?.[item?.name] && (
                    <FormHelperText error={!!errors?.[item?.name]}>
                      {<>{errors?.[item?.name]?.message}</>}
                    </FormHelperText>
                  )}
                </Fragment>
              )
            })}
          </Box>
        )}
        <PermissionSection
          isShow={isShow}
          availableUsers={availableUsers}
          departments={departments}
          permissionsIsLoading={permissionsIsLoading}
          usersIsLoading={usersIsLoading}
          departmentsIsLoading={departmentsIsLoading}
        />
      </SectionCard>
      <SectionCard title="Vencimento e revisão periódica do documento">
        {!collectAccepted ||
        (collectAccepted && currentDocument?.status === EXPIRED_STATUS_ID) ? (
          <>
            <Controller
              as={
                <FormControlLabel
                  label="Ativar data de vencimento do documento"
                  labelPlacement="end"
                  disabled={isShow}
                  control={<Switch color="primary" />}
                />
              }
              name="dueDate"
              control={control}
            />
            {dueDate && <ExpirationPickerComponent disabled={isShow} />}
          </>
        ) : (
          <Alert variant="standard" severity="info">
            Não será possível editar a data de vencimento após a coleta de
            assinaturas.
          </Alert>
        )}
      </SectionCard>
      <SectionCard title="Anexar documento com assinatura física ou padrão ICP-Brasil">
        <DocumentApprovalAttachment
          isShow={isShow}
          currentDocument={currentDocument}
        />
      </SectionCard>
      <SectionCard title="Informações do documento">
        <InformationSection
          renderMode="metadata"
          type={type}
          currentDocument={currentDocument}
        />
      </SectionCard>
    </Box>
  )
}

DocumentSettings.propTypes = {
  availableUsers: PropTypes.array,
  currentDocument: PropTypes.object,
  departments: PropTypes.array,
  departmentsIsLoading: PropTypes.bool,
  permissionsIsLoading: PropTypes.bool,
  type: PropTypes.oneOf(['create', 'edit', 'show']).isRequired,
  usersIsLoading: PropTypes.bool,
}

export default DocumentSettings
